import { useLocation } from 'react-router-dom';
import type { StructuredLine } from './resumeDocument';

export interface Suggestion {
  advice: string;
  about: string;
}

export interface ResumeEdit {
  kind: 'add' | 'rewrite' | 'replace' | 'remove';
  /** Verbatim from the CV — the line this change belongs to. */
  anchor: string;
  /** Finished wording, safe to apply as it stands. Never a question. */
  suggestion?: string;
  /** For an add the CV cannot back up yet: what we need to ask them. */
  question?: string;
  /** The line they could add, with a [gap] for each fact only they know. */
  draft?: string;
  why: string;
  /** The measured change in score if this is applied (once filled, for a draft). */
  impact?: number;
  /** The posting's terms this change brings into the CV. */
  gains?: string[];
  /** Further lines that go when this is accepted: the rest of a grouped removal, or a heading a swap replaces. */
  span?: string[];
}

/** A change that puts different words on the line it sits on. */
export const replacesLine = (kind: ResumeEdit['kind']): boolean => kind === 'rewrite' || kind === 'replace';

/**
 * The lines an accepted removal takes with it beyond its own.
 *
 * Personal details are offered as one removal of several lines; the card sits on the
 * first, and the rest are named here so every renderer drops the same set.
 */
export function droppedWith(lines: MarkedLine[], accepted: ReadonlySet<number>): Set<string> {
  const dropped = new Set<string>();
  lines.forEach(({ edit }, index) => {
    if (edit?.span && accepted.has(index)) for (const line of edit.span) dropped.add(normalise(line));
  });
  return dropped;
}

/** A [bracketed] gap in a draft that the person has not filled yet. */
export const UNFILLED = /\[[^\]]*\]/;

export interface FormatIssue {
  id: string;
  severity: 'high' | 'medium' | 'low';
  title: string;
  detail: string;
  evidence?: string;
}

export interface FormatAudit {
  score: number;
  issues: FormatIssue[];
}

export interface CheckResult {
  overallScore: number;
  verdict: 'strong_match' | 'possible_match' | 'weak_match';
  breakdown: Record<string, number>;
  matchedKeywords: string[];
  missingKeywords: string[];
  /** Matched, but only in a list or summary — they count for half until shown in work. */
  listedKeywords: string[];
  /** The posting's terms it only prefers ("a plus", "nice to have"). */
  optionalKeywords: string[];
  /** What a recruiter will notice that a parser does not. None of it moves the score. */
  checks: FormatIssue[];
  /** The written suggestions are partial: the model was too busy to answer in full. */
  limited?: boolean;
  strengths: string[];
  suggestions: Suggestion[];
  resumeText: string;
  edits: ResumeEdit[];
  /** One entry per line of `resumeText`, saying what that line is. */
  structure: StructuredLine[];
  /** What a parser struggles with in their layout, and how much it costs. */
  format: FormatAudit;
  storage: string;
  /**
   * The posting they checked against, handed back so the mark-up screen can re-score
   * the CV as changes are accepted. Absent on a result captured before it existed.
   */
  job?: { title: string | null; description: string };
}

export const VERDICT: Record<CheckResult['verdict'], { label: string; line: string; tone: string }> = {
  strong_match: {
    label: 'Strong match',
    line: 'This reads like someone the role was written for.',
    tone: 'text-positive',
  },
  possible_match: {
    label: 'Possible match',
    line: 'There is a real case here, and it is not being made loudly enough.',
    tone: 'text-warning',
  },
  weak_match: {
    label: 'Weak match',
    line: 'As written, this would struggle to get past a first pass.',
    tone: 'text-danger',
  },
};

export const BREAKDOWN_LABEL: Record<string, string> = {
  skillsMatch: 'Skills',
  experienceMatch: 'Experience',
  educationMatch: 'Education',
  keywordDensity: 'Keyword coverage',
  formatScore: 'Format',
};

export const bandColour = (value: number) =>
  value >= 70 ? 'bg-positive' : value >= 45 ? 'bg-warning' : 'bg-danger';

/**
 * The result carried between the three screens.
 *
 * In navigation state rather than a store or the server, because the whole promise of
 * this tool is that the CV is read and dropped. State that lives only as long as the
 * tab is on these pages is the honest shape for that: nothing is written down, and a
 * refresh genuinely loses it, which the pages say rather than pretend otherwise.
 */
/** The check that came before this one, when this one is a re-check after changes. */
export interface PreviousCheck {
  overallScore: number;
  breakdown: Record<string, number>;
  missingKeywords: string[];
}

export function usePreviousCheck(): PreviousCheck | null {
  const { state } = useLocation();
  const raw = (state as { previous?: Partial<PreviousCheck> } | null)?.previous;
  if (!raw || typeof raw.overallScore !== 'number') return null;
  return {
    overallScore: raw.overallScore,
    breakdown: raw.breakdown && typeof raw.breakdown === 'object' ? raw.breakdown : {},
    missingKeywords: Array.isArray(raw.missingKeywords) ? raw.missingKeywords : [],
  };
}

export function useCheckResult(): CheckResult | null {
  const { state } = useLocation();
  const raw = (state as { result?: Partial<CheckResult> } | null)?.result;
  if (!raw) return null;

  /**
   * Every list is defaulted, because the page must not depend on a field being
   * present.
   *
   * It already did, and it white-screened: a response without `edits` — an older
   * service, a field renamed, a response that came back partial — reached
   * `result.edits.length` and took the whole screen down. One absent key is not a
   * reason to show somebody an error instead of the score we did compute.
   */
  const list = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

  return {
    overallScore: typeof raw.overallScore === 'number' ? raw.overallScore : 0,
    verdict: raw.verdict ?? 'weak_match',
    breakdown: raw.breakdown && typeof raw.breakdown === 'object' ? raw.breakdown : {},
    matchedKeywords: list<string>(raw.matchedKeywords),
    missingKeywords: list<string>(raw.missingKeywords),
    listedKeywords: list<string>(raw.listedKeywords),
    optionalKeywords: list<string>(raw.optionalKeywords),
    checks: list<FormatIssue>(raw.checks),
    limited: raw.limited === true,
    strengths: list<string>(raw.strengths),
    suggestions: list<Suggestion>(raw.suggestions),
    resumeText: typeof raw.resumeText === 'string' ? raw.resumeText : '',
    edits: list<ResumeEdit>(raw.edits),
    structure: list<StructuredLine>(raw.structure),
    format: {
      score: typeof raw.format?.score === 'number' ? raw.format.score : 0,
      issues: list<FormatIssue>(raw.format?.issues),
    },
    storage: raw.storage ?? '',
    job:
      raw.job && typeof raw.job.description === 'string'
        ? { title: typeof raw.job.title === 'string' ? raw.job.title : null, description: raw.job.description }
        : undefined,
  };
}

/**
 * Which changes the person accepted, carried between the two screens.
 *
 * The set lives in the mark-up screen's state, and the template screen renders the
 * same document — so it travels in navigation state beside the result rather than
 * being decided twice. An absent list means nothing was accepted, which is the
 * safe reading: it exports their own words.
 */
export function useAcceptedIndices(): ReadonlySet<number> {
  const { state } = useLocation();
  const raw = (state as { accepted?: unknown } | null)?.accepted;
  return new Set(Array.isArray(raw) ? raw.filter((n): n is number => typeof n === 'number') : []);
}

export const normalise = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[‘’']/g, "'")
    .replace(/[“”"]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export interface MarkedLine {
  text: string;
  edit?: ResumeEdit;
}

/**
 * The CV, line by line, with each change sitting on the line it belongs to.
 *
 * Matching is on normalised text because the anchor came back through a model, which
 * will have tidied whitespace and quotes on the way. Any edit whose anchor no longer
 * finds a line is simply not shown — the server already dropped the invented ones, and
 * this is the second gate rather than the first.
 */
export function markUpLines(resumeText: string, edits: ResumeEdit[]): MarkedLine[] {
  const remaining = [...edits];

  return resumeText.split(/\r?\n/).map((text) => {
    const line = normalise(text);
    if (!line) return { text };

    const index = remaining.findIndex((e) => {
      const anchor = normalise(e.anchor);
      return anchor.length > 0 && (line.includes(anchor) || anchor.includes(line));
    });

    if (index === -1) return { text };
    const [edit] = remaining.splice(index, 1);
    return { text, edit };
  });
}

/**
 * The CV with the accepted changes applied, and only those.
 *
 * `accepted` holds the indices, into `lines`, of the changes the person has said yes
 * to. It is required rather than defaulted, because the default that would be
 * convenient here — apply everything — is the one behaviour this must not have. These
 * suggestions are written about somebody's own career, several of them are questions
 * ("if you have done X, say so"), and a tool that answers those questions on their
 * behalf and hands back a finished document has written them a CV they cannot defend.
 * Every line in the output is one they chose.
 */
export function applyEdits(lines: MarkedLine[], accepted: ReadonlySet<number>): string {
  const out: string[] = [];

  const dropped = droppedWith(lines, accepted);

  lines.forEach(({ text, edit }, index) => {
    if (dropped.has(normalise(text))) return;
    if (!edit || !accepted.has(index)) {
      out.push(text);
      return;
    }
    if (edit.kind === 'remove') return;
    if (replacesLine(edit.kind)) {
      out.push(edit.suggestion ?? text);
      return;
    }
    out.push(text);
    // The last gate before the CV. A draft with a gap still in it is advice, not a
    // line of somebody's career, and it never goes into the document.
    if (edit.suggestion && !UNFILLED.test(edit.suggestion)) out.push(edit.suggestion);
  });

  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** The indices in `lines` that carry a change, in the order they appear. */
export const editedLineIndices = (lines: MarkedLine[]): number[] =>
  lines.flatMap((line, index) => (line.edit ? [index] : []));
